/* eslint-disable no-cond-assign */
export const STORAGE_KEY = 'promptSuggestions';

export const PROMPTS_FALLBACK = [
  'Create react form with validation',
  'Technical Assistance',
  'Draft a linkedin post',
  'Build a REST API',
] as string[];

export const MODEL_ID_FOR_SUGGESTIONS = 'Llama-3_2-3B-Instruct';

export const QUERY_FALLBACK = `To create a JSON response in english language with a list of 16 prompts tailored for the user, 
we need to consider the user's details such as his profession (Software Engineer), age, 
and interest from his country, India. This will guide the crafting of relevant titles 
and descriptions.`;

export const generateQuery = (user: CF.API.Users.User, count = 16) => {
  return `Generate a valid JSON response in english language with a list of ${count} prompts tailored for the user,
    considering the user's details primarily based on Job Title, Job Role, and Interests. Country context may be considered as secondary information.
    User details:
    - Job Title: ${user?.job_title || 'Software Engineer'}
    - Job Role: ${user?.job_role || 'Write code, develop software, and solve technical problems'}
    - Interests: ${user?.intention || 'Technology, AI, and Software Development'}
    - Country: ${user?.user_detail?.country || 'USA'} (for minor regional context if relevant)
    Output should be a valid JSON object with a key "prompts" and an array of string value". Eg: {"prompts": ["Prompt 1", "Prompt 2", "Prompt 3"]}`;
};

/**
 * Sanitizes a string by removing common escape sequences and whitespace characters.
 * Handles: literal '\n', '\t', '\r', '\f', '\v', '\0', as well as actual newlines, tabs, carriage returns, form feeds, vertical tabs, and null chars.
 * @param input The string to sanitize.
 * @returns The sanitized string.
 */
export function sanitizeJSONString(input: string): string {
  const sanitized = input
    // Remove literal escape sequences (e.g., \n, \t, etc.)
    .replace(/\\n/g, '')
    .replace(/\\t/g, '')
    .replace(/\\r/g, '')
    .replace(/\\f/g, '')
    .replace(/\\v/g, '')
    .replace(/\\0/g, '')
    // Remove actual whitespace characters (optimized order)
    .replace(/[\0\t\n\v\f\r]/g, '')
    // Remove any non-breaking spaces
    .replace(/\u00A0/g, '')
    // Optionally, trim whitespace
    .trim();
  return sanitized;
}

/**
 * Extracts valid JSON objects or arrays from a given string.
 * Prioritizes content within ```json ... ``` code blocks.
 * Falls back to loose JSON object/array patterns.
 *
 * @param str - Input string possibly containing JSON
 * @returns An array of parsed JSON values (objects or arrays)
 */
export function extractAllJSON(str: string): Array<Record<'prompts', unknown> | unknown[]> {
  // 1. Extract content inside ```json ... ``` code block if present
  const codeBlockMatch = str.match(/```json([\S\s]*?)```/);
  if (codeBlockMatch) {
    str = codeBlockMatch[1].trim();
  }

  // 2. Try to parse directly
  try {
    const parsed = JSON.parse(str);
    if (
      parsed &&
      typeof parsed === 'object' &&
      !Array.isArray(parsed) &&
      'prompts' in parsed &&
      Array.isArray((parsed as { prompts?: unknown[] }).prompts)
    ) {
      return [parsed];
    }
  } catch {
    // 3. Attempt to repair the string
    const repaired = extractAndRepairJSON(str);
    if (
      repaired &&
      typeof repaired === 'object' &&
      !Array.isArray(repaired) &&
      'prompts' in repaired &&
      Array.isArray((repaired as { prompts?: unknown[] }).prompts)
    ) {
      return [repaired];
    }

    // 4. As a last resort, extract from first { to last }
    const firstCurly = str.indexOf('{');
    const lastCurly = str.lastIndexOf('}');
    if (firstCurly !== -1 && lastCurly !== -1 && lastCurly > firstCurly) {
      let subStr = str.slice(firstCurly, lastCurly + 1);
      subStr = subStr.replace(/,\s*([\]}])/g, '$1');
      // Balance curly braces
      const openCurly = (subStr.match(/{/g) || []).length;
      const closeCurly = (subStr.match(/}/g) || []).length;
      if (openCurly > closeCurly) subStr += '}'.repeat(openCurly - closeCurly);
      try {
        const obj = JSON.parse(subStr);
        if (obj && typeof obj === 'object' && !Array.isArray(obj) && 'prompts' in obj && Array.isArray((obj as { prompts?: unknown[] }).prompts)) {
          return [obj];
        }
      } catch {
        // ignore
      }
    }

    // 5. Use regex to extract all {...} objects, repair and check each
    const objectPattern = /{[^{}]*(?:{[^{}]*}[^{}]*)*}/g;
    let match: RegExpExecArray | null;
    while ((match = objectPattern.exec(str)) !== null) {
      let objStr = match[0];
      // Try to repair each object string
      let obj: unknown = null;
      try {
        obj = JSON.parse(objStr);
      } catch {
        // Try to balance curly braces
        const openCurly = (objStr.match(/{/g) || []).length;
        const closeCurly = (objStr.match(/}/g) || []).length;
        if (openCurly > closeCurly) objStr += '}'.repeat(openCurly - closeCurly);
        objStr = objStr.replace(/,\s*([\]}])/g, '$1');
        try {
          obj = JSON.parse(objStr);
        } catch {
          obj = null;
        }
      }
      if (obj && typeof obj === 'object' && !Array.isArray(obj) && 'prompts' in obj && Array.isArray((obj as { prompts?: unknown[] }).prompts)) {
        return [obj];
      }
    }
  }

  // 6. As a final fallback, if the string starts with {, try to balance curly braces and parse
  const trimmed = str.trim();
  if (trimmed.startsWith('{')) {
    let jsonStr = trimmed;
    const openCurly = (jsonStr.match(/{/g) || []).length;
    const closeCurly = (jsonStr.match(/}/g) || []).length;
    if (openCurly > closeCurly) jsonStr += '}'.repeat(openCurly - closeCurly);
    jsonStr = jsonStr.replace(/,\s*([\]}])/g, '$1');
    try {
      const obj = JSON.parse(jsonStr);
      if (obj && typeof obj === 'object' && !Array.isArray(obj) && 'prompts' in obj && Array.isArray((obj as { prompts?: unknown[] }).prompts)) {
        return [obj];
      }
    } catch {
      // ignore
    }
  }

  return [];
}

/**
 * Attempts to extract and repair the largest valid JSON object or array from a string.
 * Handles code block markers, trailing commas, and incomplete objects.
 */
export function extractAndRepairJSON(str: string): Record<string, unknown> | unknown[] | null {
  // 1. Remove code block markers
  str = str.replace(/```json|```/g, '');

  // 2. Remove literal escape sequences and actual newlines
  str = sanitizeJSONString(str);

  // 3. Remove trailing commas before closing brackets
  str = str.replace(/,\s*([\]}])/g, '$1');

  // 4. Try to find all loose JSON objects/arrays
  const looseJSONPattern = /({[\S\s]*?})|(\[[\S\s]*?])/g;
  let match: RegExpExecArray | null;
  while ((match = looseJSONPattern.exec(str)) !== null) {
    let jsonStr = match[0];
    // If the input starts with [ but the match is an object, skip
    if (str.trim().startsWith('[') && jsonStr.trim().startsWith('{')) {
      // Skip this match by not processing further
    } else {
      // If it's an array and unbalanced, try to balance
      if (jsonStr.startsWith('[')) {
        const open = (jsonStr.match(/\[/g) || []).length;
        const close = (jsonStr.match(/]/g) || []).length;
        if (open > close) jsonStr += ']'.repeat(open - close);
      }
      try {
        return JSON.parse(jsonStr);
      } catch {
        // Try to repair by removing trailing commas and balancing brackets
        let repaired = jsonStr.replace(/,\s*([\]}])/g, '$1');
        // Balance brackets
        const openCurly = (repaired.match(/{/g) || []).length;
        const closeCurly = (repaired.match(/}/g) || []).length;
        const openSquare = (repaired.match(/\[/g) || []).length;
        const closeSquare = (repaired.match(/]/g) || []).length;
        if (openCurly > closeCurly) repaired += '}'.repeat(openCurly - closeCurly);
        if (openSquare > closeSquare) repaired += ']'.repeat(openSquare - closeSquare);
        try {
          return JSON.parse(repaired);
        } catch {
          // Continue to next match
        }
      }
    }
  }

  // Always run the fallback for arrays if the input starts with [
  const trimmed = str.trim();
  if (trimmed.startsWith('[')) {
    const firstSquare = str.indexOf('[');
    const lastSquare = str.lastIndexOf(']');
    let jsonStr = firstSquare !== -1 && lastSquare > firstSquare ? str.slice(firstSquare, lastSquare + 1) : str.slice(firstSquare);
    jsonStr = jsonStr.replace(/,\s*([\]}])/g, '$1');
    // Balance brackets
    const openSquare = (jsonStr.match(/\[/g) || []).length;
    const closeSquare = (jsonStr.match(/]/g) || []).length;
    if (openSquare > closeSquare) jsonStr += ']'.repeat(openSquare - closeSquare);
    try {
      return JSON.parse(jsonStr);
    } catch {
      return null;
    }
  } else {
    // Fallback: Try the largest substring between first { and last }
    const firstCurly = str.indexOf('{');
    const lastCurly = str.lastIndexOf('}');
    if (firstCurly !== -1 && lastCurly !== -1 && lastCurly > firstCurly) {
      let jsonStr = str.slice(firstCurly, lastCurly + 1);
      jsonStr = jsonStr.replace(/,\s*([\]}])/g, '$1');
      try {
        return JSON.parse(jsonStr);
      } catch {
        // Try to remove the last object in the array
        const lastObj = jsonStr.lastIndexOf('},');
        if (lastObj !== -1) {
          jsonStr = `${jsonStr.slice(0, Math.max(0, lastObj + 1))}]}`;
          try {
            return JSON.parse(jsonStr);
          } catch {
            return null;
          }
        }
        return null;
      }
    } else if (str.trim().startsWith('{')) {
      // Handle incomplete object by balancing curly braces
      let jsonStr = str.trim();
      const openCurly = (jsonStr.match(/{/g) || []).length;
      const closeCurly = (jsonStr.match(/}/g) || []).length;
      if (openCurly > closeCurly) jsonStr += '}'.repeat(openCurly - closeCurly);
      jsonStr = jsonStr.replace(/,\s*([\]}])/g, '$1');
      try {
        return JSON.parse(jsonStr);
      } catch {
        return null;
      }
    }
  }
  return null;
}
